import {
  IDepartment,
  ICurrency,
  ICurrencyData,
  IGLAccount,
  IUser,
  IUserData,
  ICountry,
  IDepartmentData,
  IGLAccountData
} from '@/models';

import {
  IRequisitionAttachmentData,
  IRequisitionComment,
  IRequisitionCommentData,
  IRequisitionLineFieldErrorData,
  IRequisitionStatusData,
  IRequisitionSupplierAddressData,
  IRequisitionSupplierAddressFieldErrorData,
  IRequisitionSupplierContactData,
  IRequisitionSupplierContactFieldErrorData
} from '../requisition';

// API Data

export type IInventoryAdvancePaymentSupplierAddressData = IRequisitionSupplierAddressData;

export type IInventoryAdvancePaymentSupplierContactData = IRequisitionSupplierContactData;

export type IInventoryAdvancePaymentStatusData = IRequisitionStatusData;

export type IInventoryAdvancePaymentAttachmentData = IRequisitionAttachmentData;

export interface IInventoryAdvancePaymentLineData {
  id?: number;
  item: string;
  description: string;
  total_price?: number;
  account?: IGLAccountData | number | null;
  department?: IDepartmentData | number;
}

export interface IInventoryAdvancePaymentSupplierData {
  id?: number;
  name?: string;
  vendor_code?: string;
  address?: IInventoryAdvancePaymentSupplierAddressData;
  contact?: IInventoryAdvancePaymentSupplierContactData;
}

export interface IInventoryAdvancePaymentApprovalData {
  id?: number;
  status: InventoryAdvancePaymentApprovalState;
  approver: IUserData;
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface IInventoryAdvancePaymentData {
  id?: number;
  amount?: number;
  num_items?: number;
  payment?: InventoryAdvancePaymentPaymentType;
  po_number?: string;
  note?: string;
  reason?: string;
  status?: IInventoryAdvancePaymentStatusData;
  currency?: ICurrencyData | number;
  attachments?: IInventoryAdvancePaymentAttachmentData[];
  lines?: IInventoryAdvancePaymentLineData[];
  supplier?: IInventoryAdvancePaymentSupplierData;
  is_urgent?: boolean;
  is_provide_payment_detail?: boolean;
  required_date?: Date | string;
  created_at?: Date | string;
  updated_at?: Date | string;

  approvers?: IInventoryAdvancePaymentApprovalData[];
  ap_specialist?: IUserData | number;
  finance_manager?: IUserData | number | null;
  owner?: IUserData | number;
}

export type IInventoryAdvancePaymentSupplierAddressFieldErrorData = IRequisitionSupplierAddressFieldErrorData;

export type IInventoryAdvancePaymentSupplierContactFieldErrorData = IRequisitionSupplierContactFieldErrorData;

export type IInventoryAdvancePaymentLineFieldErrorData = IRequisitionLineFieldErrorData;

export interface IInventoryAdvancePaymentSupplierFieldErrorData {
  name?: string[];
  vendor_code?: string[];
  address?: IInventoryAdvancePaymentSupplierAddressFieldErrorData;
  contact?: IInventoryAdvancePaymentSupplierContactFieldErrorData;
}

export interface IInventoryAdvancePaymentFieldErrorData {
  currency?: string[];
  reason?: string[];
  required_date?: string[];
  po_number?: string[];
  is_urgent?: string[];
  note?: string[];
  attachments?: string[];
  payment?: string[];
  supplier?: IInventoryAdvancePaymentSupplierFieldErrorData;
  lines?: IInventoryAdvancePaymentLineFieldErrorData[];
}

// Business Logic Interface
export interface IInventoryAdvancePaymentSupplierAddress
  extends IInventoryAdvancePaymentSupplierAddressData {
  country?: ICountry | string;

  validate(): boolean;
}

export interface IInventoryAdvancePaymentSupplierContact
  extends IInventoryAdvancePaymentSupplierContactData {
  validate(): boolean;
}

export interface IInventoryAdvancePaymentSupplier
  extends IInventoryAdvancePaymentSupplierData {
  address?: IInventoryAdvancePaymentSupplierAddress;
  contact?: IInventoryAdvancePaymentSupplierContact;

  validate(): boolean;
}

export interface IInventoryAdvancePaymentLine
  extends IInventoryAdvancePaymentLineData {
  validate(): boolean;
}

export interface IInventoryAdvancePaymentStatus
  extends IInventoryAdvancePaymentStatusData {
  getColor(): string;
  getIcon(): string;
}

export interface IInventoryAdvancePaymentAttachment
  extends IInventoryAdvancePaymentAttachmentData {
  validate(): boolean;
}

export interface IInventoryAdvancePaymentApproval
  extends IInventoryAdvancePaymentApprovalData {
  approver: IUser;
  validate(): boolean;
}

export interface IInventoryAdvancePayment extends IInventoryAdvancePaymentData {
  status?: IInventoryAdvancePaymentStatus;
  payment?: InventoryAdvancePaymentPaymentType;
  currency?: ICurrency | number;
  account?: IGLAccount | number;
  department?: IDepartment | number;
  attachments: IInventoryAdvancePaymentAttachment[];
  lines: IInventoryAdvancePaymentLine[];
  supplier?: IInventoryAdvancePaymentSupplier;
  required_date?: Date | string;

  note?: string;
  is_provide_payment_detail?: boolean;

  approvers?: IInventoryAdvancePaymentApproval[];
  comments?: IRequisitionComment[];
  ap_specialist?: IUser | number;
  finance_manager?: IUser | number | null;
  owner?: IUser | number;

  validate(): boolean;

  // Manage payment lines
  addLine(line: IInventoryAdvancePaymentLine): void;
  removeLine(index: number): void;

  // Manage attachments
  attachFile(file: File): void;
  detachFile(file: File): void;

  /**
   * If user is selected as an approver
   */
  isUserApprover(userId: number): boolean;

  /**
   * If user is the finance manager; since only the assigned finance manager can start the process
   */
  isUserFinanceManager(userId: number): boolean;

  /**
   * If user is the AP specialist; since only the assigned ap specialist can confirm payment
   */
  isUserApSpecialist(userId: number): boolean;
}

export interface IInventoryAdvancePaymentSupplierAddressFieldError
  extends IInventoryAdvancePaymentSupplierAddressFieldErrorData {
  clearErrors(): void;
}

export interface IInventoryAdvancePaymentSupplierContactFieldError
  extends IInventoryAdvancePaymentSupplierContactFieldErrorData {
  clearErrors(): void;
}

export interface IInventoryAdvancePaymentSupplierFieldError
  extends IInventoryAdvancePaymentSupplierFieldErrorData {
  address?: IInventoryAdvancePaymentSupplierAddressFieldError;
  contact?: IInventoryAdvancePaymentSupplierContactFieldError;

  clearErrors(): void;
}

export interface IInventoryAdvancePaymentLineFieldError
  extends IInventoryAdvancePaymentLineFieldErrorData {
  clearErrors(): void;
}

export interface IInventoryAdvancePaymentFieldError
  extends IInventoryAdvancePaymentFieldErrorData {
  supplier?: IInventoryAdvancePaymentSupplierFieldError;
  lines: IInventoryAdvancePaymentLineFieldError[];

  clearErrors(): void;
}

// Constants - Used for validation
export const INVENTORY_ADVANCE_PAYMENT_LINE_ITEM_MAX_DESCRIPTION_LENGTH = 255;

export enum InventoryAdvancePaymentStatusEnum {
  Draft = 0,
  Submitted,
  InReview,
  Approval2Pending,
  Approved,
  APSpecialistAssigned,
  Rejected,
  Cancelled,
  Paid,
  FinanceManagerAssigned,
  RequestProcessed,
  InvoiceInProgress,
  InvoiceProcessed,
  ClearingInProgress,
  Cleared
}

export enum InventoryAdvancePaymentApprovalState {
  TENTATIVE = 0,
  APPROVED,
  REJECTED
}

export enum InventoryAdvancePaymentPaymentType {
  CASH = 0,
  CARD,
  CHEQUE,
  WIRE
}

export interface InventoryAdvancePaymentFilterParams {
  state?: InventoryAdvancePaymentStatusEnum;
  owner?: number;
  po_number?: string;
}
